import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import { RootState } from '../../store';
import notificationService from '../../services/notificationService';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  allowedRoles?: ('CUSTOMER' | 'BROKER' | 'ADMIN')[];
  redirectTo?: string;
  unauthenticatedRedirectTo?: string;
  /** When true, redirect unauthorized roles without showing an error toast. */
  suppressRoleNotice?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  allowedRoles = ['CUSTOMER', 'BROKER', 'ADMIN'],
  redirectTo = '/',
  unauthenticatedRedirectTo = '/login',
  suppressRoleNotice = false,
}) => {
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  const location = useLocation();
  const loginNoticeShownRef = useRef(false);
  const roleNoticeShownRef = useRef(false);

  const needsLogin = requireAuth && !isAuthenticated;
  const lacksRole =
    isAuthenticated &&
    !!user &&
    !allowedRoles.includes(user.role) &&
    !allowedRoles.includes(user.clientType as any);

  useEffect(() => {
    if (needsLogin && !loginNoticeShownRef.current) {
      loginNoticeShownRef.current = true;
      notificationService.warning('Login required', 'Please log in to access this page.');
    }
  }, [needsLogin]);

  useEffect(() => {
    if (lacksRole && !roleNoticeShownRef.current && !suppressRoleNotice) {
      roleNoticeShownRef.current = true;
      notificationService.error(
        'Access denied',
        `You don't have permission to access this page. Required roles: ${allowedRoles.join(', ')}`
      );
    }
  }, [lacksRole, allowedRoles, suppressRoleNotice]);

  // If authentication is required but user is not authenticated
  if (needsLogin) {
    return <Navigate to={unauthenticatedRedirectTo} state={{ from: location }} replace />;
  }

  // If user is authenticated but doesn't have required role
  if (lacksRole) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;